/**
 * Form submission handler.
 *
 * Coordinates the full submission flow:
 * 1. Check honeypot (reject if filled)
 * 2. Execute reCAPTCHA and get score
 * 3. Insert submission into Supabase
 * 4. Insert co-insured drivers (if any)
 * 5. Insert consent records
 * 6. Delete draft
 * 7. Trigger confirmation emails
 * 8. Track analytics event
 */

import { supabase } from "@/lib/supabase";
import { getRecaptchaToken } from "@/lib/recaptcha";
import { sendUserConfirmationEmail, sendAdminAlertEmail, sendReferralStageEmail } from "@/lib/email";
import { getReferrerCode } from "@/lib/referral";
import { trackEvent } from "@/lib/analytics";
import type { FullFormData } from "@/schemas/formSchemas";
import type {
  ExtractedPolicy,
  FieldMetadata,
} from "@/types/extraction";

export interface ExtractionSubmissionContext {
  storagePath: string;
  extractedPolicy: ExtractedPolicy;
  metadata: FieldMetadata;
}

// Consent text strings -- version these when the language changes
const CONSENT_TEXTS: Record<string, string> = {
  form_submission:
    "I authorize OneCalla to contact my carrier to review my current coverage and rates and identify potential adjustments. No changes will be made without my approval.",
  privacy_policy:
    "I have read and agree to the OneCalla Privacy Policy.",
  terms_of_service:
    "I have read and agree to the OneCalla Terms of Service.",
  card_authorization:
    "I authorize OneCalla to charge the card on file for 30% of confirmed first-year annual savings, as described in the Terms of Service. No charge will be made unless savings are confirmed by the carrier.",
};

/**
 * Compute a SHA-256 hash of a string.
 * Used to create a cryptographic record of the exact consent text shown to the user.
 */
async function sha256(text: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(text);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

interface SubmitResult {
  success: boolean;
  submissionId?: string;
  error?: string;
  userEmailStatus?: "sent" | "failed";
}

export async function submitForm(
  formData: FullFormData,
  executeRecaptcha: ((action?: string) => Promise<string>) | undefined,
  extractionContext?: ExtractionSubmissionContext,
): Promise<SubmitResult> {
  // 1. Honeypot check
  if (formData.honeypot?.website && formData.honeypot.website.length > 0) {
    // Silently reject -- don't tell bots why
    console.warn("[Submit] Honeypot triggered");
    return { success: true, submissionId: "rejected" };
  }

  // 2. reCAPTCHA: token is required; server verifies the score before we accept the submission.
  const recaptchaToken = await getRecaptchaToken("submit_form", executeRecaptcha);
  if (!recaptchaToken) {
    return { success: false, error: "Verification failed. Please refresh and try again." };
  }

  let recaptchaScore: number | null = null;
  try {
    const verifyResp = await fetch("/api/verify-recaptcha", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token: recaptchaToken }),
    });
    const verifyJson = (await verifyResp.json()) as { success?: boolean; score?: number };
    if (!verifyResp.ok || !verifyJson.success) {
      return { success: false, error: "Verification failed. Please try again." };
    }
    recaptchaScore = typeof verifyJson.score === "number" ? verifyJson.score : null;
  } catch (err) {
    console.error("[Submit] reCAPTCHA verify failed", err);
    return { success: false, error: "We couldn't verify your session. Please try again." };
  }

  // 3. Get authenticated user
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) {
    return { success: false, error: "You must be logged in to submit." };
  }

  try {
    // 4. Insert submission
    const { data: submission, error: submitError } = await supabase
      .from("submissions")
      .insert({
        user_id: user.id,
        status: "new",
        // Step 1
        carrier_name: formData.step1.carrierName === "Other"
          ? formData.step1.carrierNameOther || "Other"
          : formData.step1.carrierName,
        policy_number: formData.step1.policyNumber,
        // Step 2
        full_legal_name: formData.step2.fullLegalName,
        date_of_birth: formData.step2.dateOfBirth,
        mailing_address_street: formData.step2.mailingAddressStreet,
        mailing_address_unit: formData.step2.mailingAddressUnit || null,
        mailing_address_city: formData.step2.mailingAddressCity,
        mailing_address_state: formData.step2.mailingAddressState,
        mailing_address_zip: formData.step2.mailingAddressZip,
        phone_on_file: formData.step2.phoneOnFile,
        phone_is_current: formData.step2.phoneIsCurrent,
        phone_new: formData.step2.phoneNew || null,
        home_ownership: formData.step2.homeOwnership,
        // Step 3
        vehicle_year: formData.step3.vehicleYear,
        vehicle_make: formData.step3.vehicleMake,
        vehicle_model: formData.step3.vehicleModel,
        vehicle_purchase_date: formData.step3.vehiclePurchaseDate,
        vehicle_bought_condition: formData.step3.vehicleBoughtCondition,
        vehicle_original_purchase_method: formData.step3.vehicleOriginalPurchaseMethod,
        vehicle_current_payoff_status: formData.step3.vehicleCurrentPayoffStatus,
        vehicle_lien_holder: formData.step3.vehicleLienHolder || null,
        annual_mileage: formData.step3.annualMileage,
        primary_vehicle_use: formData.step3.primaryVehicleUse,
        commute_mileage: formData.step3.commuteMileage ?? null,
        // Step 4
        license_state: formData.step4.licenseState,
        marital_status: formData.step4.maritalStatus,
        job_title: formData.step4.jobTitle,
        employer_name: formData.step4.employerName,
        is_student: formData.step4.isStudent,
        student_school: formData.step4.studentSchool || null,
        student_program: formData.step4.studentProgram || null,
        student_full_time: formData.step4.studentFullTime ?? null,
        education_level: formData.step4.educationLevel,
        year_first_licensed: formData.step4.yearFirstLicensed,
        student_degree_pursuing: formData.step4.studentDegreePursuing ?? null,
        // Step 5
        has_co_insured: formData.step5.hasCoInsured,
        // Step 6 (all optional)
        current_premium_amount: formData.step6.currentPremiumAmount ?? null,
        current_premium_frequency: formData.step6.currentPremiumFrequency ?? null,
        competitive_quote_amount: formData.step6.competitiveQuoteAmount ?? null,
        competitive_quote_carrier: formData.step6.competitiveQuoteCarrier ?? null,
        target_price: formData.step6.targetPrice ?? null,
        has_autopay: formData.step6.hasAutopay ?? null,
        has_paperless: formData.step6.hasPaperless ?? null,
        pays_in_full: formData.step6.paysInFull ?? null,
        memberships: formData.step6.memberships ?? null,
        driving_record_has_accidents: formData.step6.drivingRecordAccidents?.hasIncident ?? null,
        driving_record_accident_details: formData.step6.drivingRecordAccidents?.details ?? null,
        driving_record_has_claims: formData.step6.drivingRecordClaims?.hasIncident ?? null,
        driving_record_claim_details: formData.step6.drivingRecordClaims?.details ?? null,
        driving_record_has_tickets: formData.step6.drivingRecordTickets?.hasIncident ?? null,
        driving_record_ticket_details: formData.step6.drivingRecordTickets?.details ?? null,
        safety_features: formData.step6.safetyFeatures ?? null,
        safety_features_other: formData.step6.safetyFeaturesOther ?? null,
        has_competitive_quote: formData.step6.hasCompetitiveQuote ?? false,
        memberships_other: formData.step6.membershipsOther ?? null,
        use_signin_email: formData.step6.useSigninEmail ?? false,
        defensive_driving_completed: formData.step6.defensiveDrivingCompleted ?? null,
        defensive_driving_date: formData.step6.defensiveDrivingDate ?? null,
        drivers_license_number: formData.step6.driversLicenseNumber ?? null,
        email_on_file: formData.step6.emailOnFile ?? null,
        other_policies: formData.step6.otherPolicies ?? null,
        // Metadata
        recaptcha_score: recaptchaScore,
        // Dec-page extraction provenance (null when user filled manually)
        dec_page_storage_path: extractionContext?.storagePath ?? null,
        extraction_metadata: extractionContext?.metadata ?? null,
        extracted_coverages: extractionContext
          ? {
              coverages: extractionContext.extractedPolicy.coverages,
              discountsApplied: extractionContext.extractedPolicy.discountsApplied,
              bundledPolicies: extractionContext.extractedPolicy.bundledPolicies,
              policy: extractionContext.extractedPolicy.policy,
              detectedIssues: extractionContext.extractedPolicy.detectedIssues,
              overallConfidence: extractionContext.extractedPolicy.overallConfidence,
            }
          : null,
      })
      .select("id")
      .single();

    if (submitError || !submission) {
      console.error("[Submit] Submission insert failed:", submitError);
      return { success: false, error: "Failed to submit. Please try again." };
    }

    const submissionId = submission.id;

    // 5a. Insert additional vehicles (index >= 1). Primary vehicle is stored
    // on the flat vehicle_* columns of submissions above.
    if (formData.step3.additionalVehicles && formData.step3.additionalVehicles.length > 0) {
      const vehicleRows = formData.step3.additionalVehicles.map((v, i) => ({
        submission_id: submissionId,
        user_id: user.id,
        vehicle_index: i + 1,
        vehicle_year: v.vehicleYear,
        vehicle_make: v.vehicleMake,
        vehicle_model: v.vehicleModel,
        vehicle_purchase_date: v.vehiclePurchaseDate,
        vehicle_bought_condition: v.vehicleBoughtCondition,
        vehicle_original_purchase_method: v.vehicleOriginalPurchaseMethod,
        vehicle_current_payoff_status: v.vehicleCurrentPayoffStatus,
        vehicle_lien_holder: v.vehicleLienHolder || null,
        annual_mileage: v.annualMileage,
        primary_vehicle_use: v.primaryVehicleUse,
        commute_mileage: v.commuteMileage ?? null,
        vin: extractionContext?.extractedPolicy.vehicles[i + 1]?.vin.value ?? null,
      }));
      const { error: vehicleError } = await supabase
        .from("policy_vehicles")
        .insert(vehicleRows);
      if (vehicleError) {
        console.error("[Submit] policy_vehicles insert failed:", vehicleError);
        // Non-blocking: primary vehicle is saved on the submission row
      }
    }

    // 5b. Insert co-insured drivers (if any)
    if (formData.step5.hasCoInsured && formData.step5.drivers?.length) {
      const driverRows = formData.step5.drivers.map((driver, index) => ({
        submission_id: submissionId,
        user_id: user.id,
        driver_index: index + 1,
        full_legal_name: driver.fullLegalName,
        date_of_birth: driver.dateOfBirth,
        phone: driver.phone || null,
        phone_same_as_primary: driver.phoneSameAsPrimary,
        mailing_address_street: driver.mailingAddressStreet || null,
        mailing_address_unit: driver.mailingAddressUnit || null,
        mailing_address_city: driver.mailingAddressCity || null,
        mailing_address_state: driver.mailingAddressState || null,
        mailing_address_zip: driver.mailingAddressZip || null,
        address_same_as_primary: driver.addressSameAsPrimary,
        license_state: driver.licenseState || null,
        license_state_same_as_primary: driver.licenseStateSameAsPrimary,
        marital_status: driver.maritalStatus || null,
        marital_status_same_as_primary: driver.maritalStatusSameAsPrimary,
        job_title: driver.jobTitle,
        employer_name: driver.employerName || null,
        employer_same_as_primary: driver.employerSameAsPrimary,
        is_student: driver.isStudent,
        student_school: driver.studentSchool || null,
        student_program: driver.studentProgram || null,
        student_full_time: driver.studentFullTime ?? null,
        relationship_to_primary: driver.relationshipToPrimary,
        education_level: driver.educationLevel ?? null,
        year_first_licensed: driver.yearFirstLicensed ?? null,
        student_degree_pursuing: driver.studentDegreePursuing ?? null,
      }));

      const { error: driverError } = await supabase
        .from("co_insured_drivers")
        .insert(driverRows);

      if (driverError) {
        console.error("[Submit] Co-insured insert failed:", driverError);
        // Non-blocking: submission was saved, drivers can be added manually
      }
    }

    // 6. Insert consent records with SHA-256 hashes of exact text shown
    const consentTypes: Array<{ type: string; consented: boolean }> = [
      { type: "form_submission", consented: formData.consent.consentCarrierContact },
      { type: "privacy_policy", consented: formData.consent.consentPrivacyPolicy },
      { type: "terms_of_service", consented: formData.consent.consentTermsOfService },
    ];
    if (formData.consent.consentCardCharge) {
      consentTypes.push({ type: "card_authorization", consented: true });
    }

    const consentRows = await Promise.all(
      consentTypes
        .filter((c) => c.consented)
        .map(async (c) => ({
          user_id: user.id,
          consent_type: c.type,
          consent_text_hash: await sha256(CONSENT_TEXTS[c.type] || c.type),
          consent_version: "v1.1",
          // ip_address and user_agent should be captured server-side
        }))
    );

    if (consentRows.length > 0) {
      const { error: consentError } = await supabase
        .from("consent_records")
        .insert(consentRows);

      if (consentError) {
        console.error("[Submit] Consent insert failed:", consentError);
      }
    }

    // 6b. Referral attribution. If the user landed here with ?r=<code> (or had
    // one in localStorage from a prior visit within 30 days), link this
    // submission to a referrer and fire R1. Attribution failures never block
    // the submission -- the user never knows a referral code is missing.
    const referrerCode = getReferrerCode();
    if (referrerCode) {
      try {
        const { data: referrer } = await supabase
          .from("referrers")
          .select("id, code, commission_rate, name, email")
          .eq("code", referrerCode)
          .eq("status", "active")
          .maybeSingle();

        if (referrer) {
          const { data: newReferral, error: referralError } = await supabase
            .from("referrals")
            .insert({
              referrer_id: referrer.id,
              referrer_code: referrer.code,
              source: "intake",
              source_submission_id: submissionId,
              referred_name: formData.step2.fullLegalName,
              referred_email: formData.step6.emailOnFile || user.email || null,
              commission_rate: referrer.commission_rate,
              stage: "signed_up",
            })
            .select("id")
            .single();

          if (referralError) {
            console.error("[Submit] Referral insert failed:", referralError);
          } else {
            // Fire R1 (friend just signed up) -- non-blocking. The referralIds
            // lets the server stamp last_notified_stage so the reconciliation
            // cron does not re-send R1 on its next run.
            sendReferralStageEmail("signed_up", {
              referrerEmail: referrer.email,
              referrerName: referrer.name,
              referrerCode: referrer.code,
              referralIds: newReferral ? [newReferral.id] : undefined,
              referredFirstName: formData.step2.fullLegalName.split(" ")[0] || "your friend",
            }).catch((err) => console.error("[Email] R1 send failed", err));
            trackEvent("referral_attributed", {
              submission_id: submissionId,
              referrer_code: referrer.code,
            });
          }
        }
      } catch (err) {
        console.error("[Submit] Referral attribution error (non-blocking):", err);
      }
    }

    // 7. Delete draft
    await supabase.from("drafts").delete().eq("user_id", user.id);
    try {
      localStorage.removeItem("onecalla_draft_backup");
    } catch {
      // ignore
    }

    // 8. Send emails (non-blocking)
    const vehicleInfo = `${formData.step3.vehicleYear} ${formData.step3.vehicleMake} ${formData.step3.vehicleModel}`;
    const emailData = {
      userId: user.id,
      userName: formData.step2.fullLegalName,
      userEmail: formData.step6.emailOnFile || user.email || "",
      carrierName: formData.step1.carrierName,
      vehicleInfo,
      premiumAmount: formData.step6.currentPremiumAmount ?? undefined,
      premiumFrequency: formData.step6.currentPremiumFrequency ?? undefined,
      submissionId,
    };

    // Fire the user confirmation and capture its outcome for the success screen.
    // Admin alert stays fire-and-forget (admin visibility handled via Resend dashboard).
    const userEmailPromise: Promise<"sent" | "failed"> = sendUserConfirmationEmail(emailData)
      .then(() => "sent" as const)
      .catch((err) => {
        console.error("[Email] User confirmation send failed", err);
        trackEvent("email_send_failed", {
          submission_id: submissionId,
          kind: "user_confirmation",
          error: err instanceof Error ? err.message : String(err),
        });
        return "failed" as const;
      });

    sendAdminAlertEmail(emailData).catch((err) => {
      console.error("[Email] Admin alert send failed", err);
      trackEvent("email_send_failed", {
        submission_id: submissionId,
        kind: "admin_alert",
        error: err instanceof Error ? err.message : String(err),
      });
    });

    const userEmailStatus = await userEmailPromise;

    // 9. Track analytics
    trackEvent("form_submitted", {
      submission_id: submissionId,
      carrier: formData.step1.carrierName,
      has_co_insured: formData.step5.hasCoInsured,
      user_email_status: userEmailStatus,
    });

    return { success: true, submissionId, userEmailStatus };
  } catch (error) {
    console.error("[Submit] Unexpected error:", error);
    return { success: false, error: "An unexpected error occurred. Please try again." };
  }
}
